import {
    ApolloClient as Client,
    ApolloLink,
    ApolloProvider,
    FetchResult,
    HttpLink,
    InMemoryCache,
    Observable,
    Operation,
    split,
} from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { print } from 'graphql';
import { createClient, type Client as SSEClient, type ClientOptions } from 'graphql-sse';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { useState } from 'react';
import { Navigate } from 'react-router-dom';

import Cookies from 'js-cookie';
import posthog from 'posthog-js';
import { COOKIES, PATHS_NO_SUBSCRIPTION_REQUIRED } from '@/constants';

const BETTER_AUTH_UI_ENABLED = ['1', 'true', 'yes', 'on'].includes(
    (import.meta.env.VITE_BETTER_AUTH_UI_ENABLED || '').toLowerCase(),
);

class SSELink extends ApolloLink {
    private client: SSEClient;

    constructor(options: ClientOptions) {
        super();
        this.client = createClient(options);
    }

    public request(operation: Operation): Observable<FetchResult> {
        return new Observable((sink) => {
            const unsubscribe = this.client.subscribe(
                { ...operation, query: print(operation.query) },
                {
                    next: (value) => sink.next(value as FetchResult),
                    complete: sink.complete.bind(sink),
                    error: sink.error.bind(sink),
                },
            );
            return unsubscribe;
        });
    }
}

export enum GraphQLKnownErrorCodes {
    FORBIDDEN = 'FORBIDDEN',
    SUBSCRIPTION_REQUIRED = 'SUBSCRIPTION_REQUIRED',
    TEAM_PERMISSION_REQUIRED = 'TEAM_PERMISSION_REQUIRED',
    INVALID_AUTH_PUBLIC = 'INVALID_AUTH_PUBLIC',
}
export const GRAPHQL_KNOWN_ERROR_CODES = Object.values(GraphQLKnownErrorCodes);

type ForbiddenTypes = `${GraphQLKnownErrorCodes}`;

// Cursor (keyset) merge for reverse-paginated lists (post chat / activity). All pages for a given
// keyArgs (e.g. postId+channel) share one cache entry; every write — initial load, older `before`
// page, newer `after` page, jump-to-reply `untilId` gap-fill, socket refetch, optimistic send — is
// merged into a single list deduped by id and sorted oldest→newest (the UI renders newest at bottom).
// `hasMore` tracks "older items remain": older/gap fetches set it; a latest/socket refetch keeps the
// already-known value so it isn't clobbered back to true after the top is reached.
const cursorMergeOptions = (keyArgs: string[]) => {
    return {
        keyArgs,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        merge(existing: any, incoming: any, { args, readField }: any) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const existingItems: any[] = existing?.items ?? [];
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const incomingItems: any[] = incoming?.items ?? [];

            // A plain head fetch (initial load or socket refetch — no cursor) is authoritative for the
            // newest window: any existing (non-optimistic) message within that window but absent from
            // `incoming` was deleted server-side, so drop it. Older history (before the window) and
            // in-flight optimistic messages are preserved. Cursor pages stay a pure union.
            const isHeadFetch = args?.before == null && args?.after == null && args?.untilId == null;
            const incomingIds = new Set<unknown>();
            let oldestIncoming: string | null = null;
            incomingItems.forEach((ref) => {
                const id = readField('id', ref);
                if (id != null) incomingIds.add(id);
                const createdAt = readField('createdAt', ref) as string | undefined;
                if (createdAt != null && (oldestIncoming == null || createdAt < oldestIncoming)) {
                    oldestIncoming = createdAt;
                }
            });

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const byId = new Map<unknown, any>();
            existingItems.forEach((ref) => {
                const id = readField('id', ref);
                if (id == null) return;
                if (isHeadFetch && oldestIncoming != null && !incomingIds.has(id)) {
                    const createdAt = readField('createdAt', ref) as string | undefined;
                    const optimistic = typeof id === 'string' && id.startsWith('optimistic:');
                    if (!optimistic && createdAt != null && createdAt >= oldestIncoming) {
                        return; // within the authoritative window but gone server-side → deleted
                    }
                }
                byId.set(id, ref);
            });
            incomingItems.forEach((ref) => {
                const id = readField('id', ref);
                if (id != null) byId.set(id, ref);
            });

            const items = [...byId.values()].sort((a, b) => {
                const ca = (readField('createdAt', a) as string) ?? '';
                const cb = (readField('createdAt', b) as string) ?? '';
                return ca < cb ? -1 : ca > cb ? 1 : 0;
            });

            const loadedOlder = args?.before != null || args?.untilId != null;
            const hasMore = loadedOlder
                ? incoming.hasMore
                : args?.after != null
                  ? (existing?.hasMore ?? incoming.hasMore)
                  : existing
                    ? existing.hasMore
                    : incoming.hasMore;

            return { ...incoming, items, hasMore };
        },
    };
};

const loadMoreOptions = (keyArgs: string[] = []) => {
    return {
        keyArgs: keyArgs, //[...keyArgs, 'orderBy'],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        merge(existing: any, incoming: any, { args: { skip = 0 } }: any) {
            if (skip === 0) {
                return incoming;
            }

            return {
                items: [...(existing?.items ?? []), ...incoming.items],
                total: skip === 0 ? incoming.total : existing?.total,
                hasMore: incoming.hasMore,
            };
        },
    };
};

function makeClient(setForbidden: (val: false | ForbiddenTypes) => void, shareToken?: string) {
    // Link to add PostHog distinct ID header
    const customHeadersLink = setContext((_, { headers }) => {
        const distinctId = typeof posthog !== 'undefined' ? posthog.get_distinct_id() : '';
        return {
            headers: {
                ...headers,
                'x-posthog-distinct-id': distinctId || '',
            },
        };
    });

    // Link to validate if user is logged in
    const verifyErrorCodesLink = onError(({ response }) => {
        if (
            response?.errors &&
            response?.errors?.length > 0 &&
            typeof response?.errors[0].extensions?.code === 'string'
        ) {
            const errorCode = response?.errors[0].extensions?.code;
            const message = response?.errors[0].message;

            // Send error to puppeteer or external listeners
            window.postMessage(
                {
                    type: 'GRAPHQL_ERROR',
                    errorCode,
                    message: response?.errors?.[0]?.message,
                    origin: window.origin,
                },
                window.origin,
            );

            if (
                errorCode === GraphQLKnownErrorCodes.FORBIDDEN ||
                errorCode === GraphQLKnownErrorCodes.SUBSCRIPTION_REQUIRED ||
                errorCode === GraphQLKnownErrorCodes.TEAM_PERMISSION_REQUIRED
            ) {
                setForbidden(errorCode);

                // since we are going to redirect the user to a dedicated page, don't fire onError of queries
                // prevent unnecessary messages of a request that has onError implement with a toaster
                return new Observable((observer) => {
                    observer.next({ data: undefined });
                    observer.complete();
                });
            } else if (message === GraphQLKnownErrorCodes.INVALID_AUTH_PUBLIC) {
                setForbidden(message);
                return new Observable((observer) => {
                    observer.next({ data: undefined });
                    observer.complete();
                });
            }
        }
    });

    const posthog_session_id = posthog.get_session_id();

    const httpLink = new HttpLink({
        uri: import.meta.env.VITE_API_ROOT + '/graphql',
        // you can disable result caching here if you want to
        // (this does not work if you are rendering your page with `export const dynamic = "force-static"`)
        fetchOptions: { cache: 'no-store' },
        credentials: 'include',
        headers: {
            ...(shareToken && { 'share-token': shareToken }),
            ...(posthog_session_id && { 'x-posthog-session-id': posthog_session_id }),
        },
        // you can override the default `fetchOptions` on a per query basis
        // via the `context` property on the options passed as a second argument
        // to an Apollo Client data fetching hook, e.g.:
        // const { data } = useSuspenseQuery(MY_QUERY, { context: { fetchOptions: { cache: "force-cache" }}});
    });

    const sseLink = new SSELink({
        url: import.meta.env.VITE_API_ROOT + '/andie/stream',
        credentials: 'include',
        headers: () => {
            const distinctId = typeof posthog !== 'undefined' ? posthog.get_distinct_id() : '';
            const sessionId = posthog.get_session_id();
            return {
                ...(distinctId && { 'x-posthog-distinct-id': distinctId }),
                ...(sessionId && { 'x-posthog-session-id': sessionId }),
                ...(shareToken && { 'share-token': shareToken }),
            };
        },
    });

    const httpChain = customHeadersLink.concat(verifyErrorCodesLink).concat(httpLink);
    const apolloLink = split(
        ({ query }) => {
            const definition = getMainDefinition(query);
            return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
        },
        sseLink,
        httpChain,
    );

    return new Client({
        cache: new InMemoryCache({
            typePolicies: {
                Query: {
                    fields: {
                        workspaces: loadMoreOptions(),
                        notifications: loadMoreOptions(),
                        groupNotifications: loadMoreOptions(['group']),
                        dashs: loadMoreOptions(['type', 'dashsIds']),
                        templateWidgets: loadMoreOptions(),
                        dashSourceProfiles: loadMoreOptions(['dashId', 'dashSourceGroup']),
                        netProfileAuths: loadMoreOptions(['statusLevels']),
                        // Each filter combination is its own list, otherwise a filtered or take: 1 list replaces the full one.
                        groups: loadMoreOptions(['includeEmpty', 'onlyPublish', 'search', 'take']),
                        insightsCategories: loadMoreOptions(),
                        ideas: loadMoreOptions(),
                        labels: loadMoreOptions(['search']),
                        postsViews: loadMoreOptions(),
                        postMessages: cursorMergeOptions(['postId', 'channel']),
                        postActivity: cursorMergeOptions(['postId']),
                    },
                },
                SubscriptionPreview: {
                    keyFields: [],
                },
                DashSourceProfile: {
                    keyFields: ['id', 'dashSource'],
                },
                WidgetData: {
                    keyFields: false,
                },
                AuthPublicToken: {
                    keyFields: false,
                },
                Total: {
                    keyFields: ['day', 'stage'],
                },
            },
        }),
        link: apolloLink,
    });
}

export function ApolloClient({ children }: React.PropsWithChildren) {
    const [forbidden, setForbidden] = useState<false | ForbiddenTypes>(false);
    // Credential for a shared link: either an anonymous public-view token or a named guest
    // token. The API tells them apart by the token's payload, so one header carries both.
    const shareToken = new URLSearchParams(window.location.search).get('token') || undefined;

    // we use window.location.pathname & search because useLocation causes wasted rerenders (known reported issue of react-router)

    let navigateTo: JSX.Element | null = null;
    if (forbidden === GraphQLKnownErrorCodes.FORBIDDEN) {
        // in case of user is not logged in while making a request, redirect to auth
        // send full pathname & search because auth app may need this info

        const fullPath = `${window.location.pathname}${window.location.search}`;
        Cookies.set(COOKIES.REDIRECT_AFTER_LOGIN, fullPath, { expires: 1 });

        if (BETTER_AUTH_UI_ENABLED) {
            window.location.href = `/auth/login?redirectTo=${encodeURIComponent(fullPath)}`;
        } else {
            window.location.href = `${import.meta.env.VITE_LEGACY_APP_ROOT}${fullPath}`;
        }
    } else if (forbidden === GraphQLKnownErrorCodes.SUBSCRIPTION_REQUIRED) {
        // in case of user not having an active subscription, redirect to expired page.
        // don't force it while the user is on a route that doesn't require a subscription
        // (billing/settings/subscribe) so they can still add billing info & subscribe.
        const routeAllowed = PATHS_NO_SUBSCRIPTION_REQUIRED.some((path) =>
            window.location.pathname.startsWith(path),
        );
        if (!routeAllowed) {
            navigateTo = <Navigate to={'/settings/subscription-expired'} />;
        }
    } else if (forbidden === GraphQLKnownErrorCodes.TEAM_PERMISSION_REQUIRED) {
        navigateTo = <Navigate to={'/team-access-denied'} />;
    } else if (forbidden === GraphQLKnownErrorCodes.INVALID_AUTH_PUBLIC) {
        navigateTo = <Navigate to={'/expired'} />;
    }

    return (
        <ApolloProvider client={makeClient(setForbidden, shareToken)}>
            {navigateTo}
            {children}
        </ApolloProvider>
    );
}
